import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useApi } from "../../api/client";
import AdminBar from "../../components/AdminBar";
import { AllRoutes } from "../../config/routes";
import { ProjectSummary } from "../../types/projects";
import { formatDate, formatHours, formatMoney } from "../../utils/format";

interface ProjectForm {
  name: string;
  startDate: Dayjs | null;
  hourlyRate: string;
}

const emptyForm: ProjectForm = {
  name: "",
  startDate: dayjs(),
  hourlyRate: "",
};

const Projects: React.FC = () => {
  const { request } = useApi();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectSummary | null>(null);
  const [form, setForm] = useState<ProjectForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    try {
      setError(null);
      const data = await request<ProjectSummary[]>("/api/projects");
      setProjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (project: ProjectSummary) => {
    setEditing(project);
    setForm({
      name: project.name,
      startDate: dayjs(project.startDate),
      hourlyRate: String(project.hourlyRate),
    });
    setDialogOpen(true);
  };

  const saveProject = async () => {
    if (!form.name.trim() || !form.startDate) {
      setError("Name and start date are required");
      return;
    }
    const hourlyRate = Number(form.hourlyRate);
    if (!Number.isFinite(hourlyRate) || hourlyRate < 0) {
      setError("Hourly rate must be a number of 0 or more");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        startDate: form.startDate.toISOString(),
        hourlyRate,
      };
      if (editing) {
        await request(`/api/projects/${editing.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      } else {
        await request("/api/projects", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      setDialogOpen(false);
      await loadProjects();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  const deleteProject = async (project: ProjectSummary) => {
    if (
      !window.confirm(
        `Delete "${project.name}" and all of its time entries?`,
      )
    ) {
      return;
    }
    try {
      await request(`/api/projects/${project.id}`, { method: "DELETE" });
      await loadProjects();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete project");
    }
  };

  const copyPublicLink = async (projectId: string) => {
    const url = `${window.location.origin}${AllRoutes.Hours}/${projectId}`;
    await navigator.clipboard.writeText(url);
    setSnackbar("Public hours link copied");
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <AdminBar title="Projects" />
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h3" sx={{ mb: 0 }}>
            Projects
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            New project
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Project</TableCell>
                <TableCell>Start date</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Total hours</TableCell>
                <TableCell align="right">Unpaid hours</TableCell>
                <TableCell align="right">Unpaid</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7}>Loading projects…</TableCell>
                </TableRow>
              ) : projects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    No projects yet. Create one to start logging hours.
                  </TableCell>
                </TableRow>
              ) : (
                projects.map((project) => (
                  <TableRow key={project.id} hover>
                    <TableCell>
                      <Button
                        component={RouterLink}
                        to={`${AllRoutes.Projects}/${project.id}`}
                        sx={{ textTransform: "none", px: 0 }}
                      >
                        {project.name}
                      </Button>
                    </TableCell>
                    <TableCell>{formatDate(project.startDate)}</TableCell>
                    <TableCell align="right">
                      {formatMoney(project.hourlyRate)}
                    </TableCell>
                    <TableCell align="right">
                      {formatHours(project.totals.totalHours)}
                    </TableCell>
                    <TableCell align="right">
                      {formatHours(project.totals.pendingHours)}
                    </TableCell>
                    <TableCell align="right">
                      {formatMoney(project.totals.pendingAmount)}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label="Copy public link"
                        onClick={() => copyPublicLink(project.id)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="Edit project"
                        onClick={() => openEdit(project)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="Delete project"
                        onClick={() => deleteProject(project)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </Container>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editing ? "Edit project" : "New project"}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
          <TextField
            label="Project name"
            value={form.name}
            onChange={(event) =>
              setForm((current) => ({ ...current, name: event.target.value }))
            }
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
          />
          <DatePicker
            label="Start date"
            value={form.startDate}
            onChange={(value) =>
              setForm((current) => ({ ...current, startDate: value }))
            }
          />
          <TextField
            label="Hourly rate"
            type="number"
            value={form.hourlyRate}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                hourlyRate: event.target.value,
              }))
            }
            inputProps={{ min: 0, step: "0.01" }}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveProject} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={2500}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Box>
  );
};

export default Projects;
